import type { Envelope, LensDocumentData } from '~/pages/LeLens/document/types';

import { request } from '~/services/common';

export async function loadLensDocumentStream(
  url: string,
  options: { signal: AbortSignal; headers: Record<string, string>; body: string },
  onPartial: (data: LensDocumentData) => void,
  timeoutMs: number,
  timeoutMessage: string,
): Promise<LensDocumentData> {
  const controller = new AbortController();
  let timedOut = false;
  const abort = () => controller.abort();
  options.signal.addEventListener('abort', abort, { once: true });
  if (options.signal.aborted) abort();
  const timer = window.setTimeout(() => {
    timedOut = true;
    abort();
  }, timeoutMs);
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  try {
    const response = (await request(url, { ...options, method: 'POST', signal: controller.signal, returnResponse: true })) as Response;
    if (!response.headers.get('content-type')?.includes('application/x-ndjson')) return (await response.json()) as LensDocumentData;
    reader = response.body?.getReader();
    if (!reader) throw new Error('No Lens data stream was returned. Please retry.');
    const decoder = new TextDecoder();
    let buffered = '';
    let partial: LensDocumentData | undefined;
    let complete: LensDocumentData | undefined;
    const readLine = (line: string) => {
      if (!line.trim()) return;
      const { type, ...event } = JSON.parse(line);
      if (type === 'error') throw new Error(event.message);
      if (type === 'complete') complete = event as LensDocumentData;
      else if (type === 'plan') partial = event as LensDocumentData;
      else if (type === 'query' && partial) partial = { ...partial, queries: { ...partial.queries, [event.query]: event.envelope as Envelope } };
      if ((type === 'plan' || type === 'query') && partial && !controller.signal.aborted) onPartial(partial);
    };
    while (!controller.signal.aborted) {
      const { done, value } = await reader.read();
      buffered += decoder.decode(value, { stream: !done });
      const lines = buffered.split('\n');
      buffered = lines.pop() ?? '';
      lines.forEach(readLine);
      if (done) {
        readLine(buffered);
        break;
      }
    }
    if (controller.signal.aborted) throw new Error('Lens data loading was cancelled.');
    if (!complete) throw new Error('The Lens data stream ended before all results arrived. Please retry.');
    return complete;
  } catch (error) {
    if (timedOut) throw new Error(timeoutMessage, { cause: error });
    throw error;
  } finally {
    window.clearTimeout(timer);
    options.signal.removeEventListener('abort', abort);
    await reader?.cancel().catch(() => undefined);
  }
}
